import React from 'react';
import { Link } from 'react-router-dom';

import { Footer } from './components';
import { ROUTE } from 'src/constants/routes';

import './legal.scss';

const CONTACT_EMAIL = 'ruleenginebot@gmail.com';
const EFFECTIVE_DATE = 'August 12, 2026';

export const Terms = () => {
  return (
    <div className="legal-page">
      <main className="legal-page__main">
        <div className="legal-page__container">
          <h1 className="legal-page__title">Terms of Service</h1>
          <p className="legal-page__meta">
            Effective date: <strong>{EFFECTIVE_DATE}</strong>
          </p>

          <section className="legal-page__section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using <strong>RulesEngineBot</strong> ("Service", "we", "us", "our"),
              you agree to be bound by these Terms of Service ("Terms"). If you do not agree to
              these Terms, please do not use the Service.
            </p>
            <p>
              These Terms apply to all users, including streamers and viewers who interact with the
              bot on the Twitch platform.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>2. Description of Service</h2>
            <p>
              RulesEngineBot is a Twitch automation service that helps streamers manage command
              logic, viewer eligibility, giveaway workflows, and Chaos Mode interactions powered by
              Twitch Channel Points.
            </p>
            <p>
              The Service includes multiple contexts: public landing pages, authenticated dashboard
              pages, public embed pages (for giveaway overlays and Chaos Widget views), and an
              optional desktop application used for local integrations.
            </p>
            <p>
              The Service is currently in a controlled testing phase. Access scope, available
              features, and performance characteristics may be updated, limited, or revoked at our
              discretion.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>3. Account Registration and Twitch OAuth</h2>
            <p>
              To use the Service, you must authenticate through your Twitch account via Twitch's
              OAuth 2.0 authorization flow. By connecting your Twitch account, you grant us
              permission to:
            </p>
            <ul>
              <li>Read your Twitch channel and broadcaster information</li>
              <li>Read chat messages in your channel</li>
              <li>Send messages to your Twitch chat through the bot account</li>
              <li>Access live stream status information</li>
              <li>
                Access Channel Points reward metadata needed for Chaos Mode reward-to-action mapping
              </li>
            </ul>
            <p>
              You are responsible for maintaining the security of your Twitch account. We are not
              liable for any loss or damage resulting from unauthorized access to your account.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>4. Access Control and Team Responsibility</h2>
            <p>
              Some accounts may have role-based access restrictions to platforms and pages. Account
              owners are responsible for granting, reviewing, and revoking team access rights.
            </p>
            <p>
              You agree to keep access lists accurate and remove access for users who no longer
              require it. We are not responsible for actions taken by users who were intentionally
              granted access by your account owner or administrators.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable local, national, or international law or regulation</li>
              <li>Violate Twitch's Terms of Service or Community Guidelines</li>
              <li>Harass, abuse, threaten, or harm any person through automated chat messages</li>
              <li>Spam, flood, or disrupt Twitch chats using bot commands</li>
              <li>Attempt to gain unauthorized access to any part of the Service or its servers</li>
              <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
              <li>Resell or sublicense access to the Service without our written consent</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate access to any user who violates these
              rules.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>6. Twitch Platform Dependencies</h2>
            <p>
              Certain Service features depend on Twitch platform capabilities and policies. For
              example, custom Channel Points rewards for Chaos Mode require Twitch Affiliate or
              Partner status.
            </p>
            <p>
              If Twitch APIs, permissions, availability, or policies change, related features may be
              limited or unavailable. Such limitations do not constitute a breach of these Terms.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>7. Intellectual Property</h2>
            <p>
              All content, branding, software, and documentation of the Service is the exclusive
              property of RulesEngineBot and its licensors. You may not copy, reproduce,
              redistribute, or create derivative works without explicit written permission.
            </p>
            <p>
              You retain ownership of the content (commands, bot responses, giveaway rules) you
              create within the Service. By using the Service, you grant us a limited, non-exclusive
              license to process that content solely to provide the Service.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>8. Disclaimer of Warranties</h2>
            <p>
              The Service is provided on an <strong>"as is"</strong> and{' '}
              <strong>"as available"</strong> basis, without warranties of any kind, either express
              or implied, including but not limited to merchantability, fitness for a particular
              purpose, or non-infringement.
            </p>
            <p>
              We do not guarantee that the Service will be uninterrupted, error-free, or free of
              viruses or other harmful components.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, RulesEngineBot shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, including
              but not limited to loss of data, revenue, goodwill, or use, arising out of or in
              connection with your use of the Service.
            </p>
            <p>
              Our total liability for any claim arising from your use of the Service is limited to
              the maximum extent permitted by applicable law.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>10. Fees and Testing Phase</h2>
            <p>
              During the current controlled testing phase, approved demo users may receive access
              without in-app payment. This temporary arrangement may change at any time.
            </p>
            <p>
              If paid plans are introduced later, pricing, billing terms, and refund rules will be
              presented before purchase.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without
              cause or notice, effective immediately. Upon termination, your right to use the
              Service ceases immediately, and we may delete your data in accordance with our Privacy
              Policy.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>12. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be posted on this
              page with an updated effective date. Continued use of the Service after changes
              constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>13. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with applicable law, without
              regard to conflict of law principles. Any disputes shall first be attempted to be
              resolved amicably by contacting us.
            </p>
          </section>

          <section className="legal-page__section">
            <h2>14. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="legal-page__link">
                {CONTACT_EMAIL}
              </a>
            </p>
          </section>

          <div className="legal-page__footer-nav">
            <Link to={ROUTE.PRIVACY} className="legal-page__link">
              Privacy Policy →
            </Link>
            <Link to={ROUTE.MAIN} className="legal-page__link">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
